import { usePatientCoverages } from '@ctw/shared/api/fhir/coverages';
import type { CoverageModel } from '@ctw/shared/api/fhir/models/coverage';
import { Badge } from '@ctw/shared/components/badge';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { ResponsiveSourceDocumentLink } from '@ctw/shared/content/CCDA/responsive-source-document-link';
import type { UseResourceDrawerOpenDrawerArgs } from '@ctw/shared/context/resource-drawer-context';
import { formatDaysAgo } from '@ctw/shared/utils/dates';
import { tw } from '@ctw/shared/utils/tailwind';
import { capitalize, compact, groupBy } from 'lodash-es';
import { useMemo, useState } from 'react';

export const coverageDrawerAction: UseResourceDrawerOpenDrawerArgs<'Coverage'>['action'] = ({
  model,
}) => ({
  label: 'Coverage',
  title: model.payorDisplay ? cleanPayorDisplay(model.payorDisplay) : 'Coverage',
  content: {
    body: <CoverageDrawerBody model={model} />,
  },
});

function CoverageDrawerBody({ model }: { model: CoverageModel }) {
  const [expanded, setExpanded] = useState(false);
  const coveragesQuery = usePatientCoverages(expanded);

  const initialGroup = useMemo(() => groupCoveragesByPayor([model])[0], [model]);

  const allGroups = useMemo(
    () => (coveragesQuery.data ? groupCoveragesByPayor(coveragesQuery.data) : []),
    [coveragesQuery.data],
  );

  const otherGroups = useMemo(
    () => allGroups.filter((g) => g.payorKey !== initialGroup?.payorKey),
    [allGroups, initialGroup?.payorKey],
  );

  return (
    <div className={tw`flex flex-col gap-5`}>
      {initialGroup && <CoverageGroupCard group={initialGroup} />}

      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className={tw`cursor-pointer text-left text-primary-text text-sm hover:text-primary-main`}
        >
          See all plans
        </button>
      )}

      {expanded && coveragesQuery.isLoading && (
        <LoadingSpinner centered={true} message="Loading plans..." />
      )}

      {expanded && coveragesQuery.isError && (
        <div className={tw`text-content-subtle text-sm`}>Unable to load coverage data</div>
      )}

      {expanded && otherGroups.length > 0 && (
        <>
          <div className={tw`text-content-subtle text-sm`}>
            {otherGroups.length} other {otherGroups.length === 1 ? 'plan' : 'plans'}
          </div>
          {otherGroups.map((group) => (
            <CoverageGroupCard key={group.payorKey} group={group} />
          ))}
        </>
      )}

      {expanded && coveragesQuery.data && otherGroups.length === 0 && (
        <div className={tw`text-content-subtle text-sm`}>No other plans found</div>
      )}
    </div>
  );
}

interface CoverageGroup {
  payorKey: string;
  payorDisplay: string;
  payorDetail: string | undefined;
  rankLabel: string | undefined;
  typeDisplay: string;
  status: string;
  periodStart: string | undefined;
  coverages: Array<CoverageModel>;
}

function groupCoveragesByPayor(coverages: Array<CoverageModel>): Array<CoverageGroup> {
  /*
    Coverage resources typically exist in multitudes and it would be redundant to list them all.
    Instead, group them by the payor and select a "best" representative resource to grab metadata from.
  */
  const grouped = groupBy(coverages, (c) => c.payorDisplay ?? 'Unknown');
  return Object.entries(grouped).map(([rawPayor, items]) => {
    const representative = items.reduce((best, c) =>
      c.order !== undefined && (best.order === undefined || c.order < best.order) ? c : best,
    );
    const cleaned = cleanPayorDisplay(rawPayor);
    const parenthetical = extractParenthetical(rawPayor);
    return {
      payorKey: rawPayor,
      payorDisplay: cleaned,
      payorDetail: parenthetical,
      rankLabel: representative.rankLabel,
      typeDisplay: representative.typeDisplay,
      status: capitalize(representative.status),
      periodStart: representative.periodStart,
      coverages: items,
    };
  });
}

export function cleanPayorDisplay(raw: string): string {
  // Remove trailing parentheticals from the payor display soup
  // "WELLPOINT TX - STAR (MEDICAID REPLACEMENT - HMO)" → "WELLPOINT TX - STAR"
  return raw.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

export function extractParenthetical(raw: string): string | undefined {
  // Extract JUST the parenthetical from the payor display soup
  // "WELLPOINT TX - STAR (MEDICAID REPLACEMENT - HMO)" → "MEDICAID REPLACEMENT - HMO"
  const match = raw.match(/\(([^)]*)\)\s*$/);
  return match ? match[1] : undefined;
}

function CoverageGroupCard({ group }: { group: CoverageGroup }) {
  const metaSegments = compact([
    group.typeDisplay,
    group.status,
    group.periodStart ? `Since ${group.periodStart}` : undefined,
  ]);

  const sourceCoverages = useMemo(() => coveragesWithSources(group.coverages), [group.coverages]);

  return (
    <ContentCard variant="focal">
      <div className={tw`flex flex-col gap-3`}>
        <div className={tw`flex items-start justify-between gap-2`}>
          <div className={tw`flex flex-col`}>
            <span className={tw`font-medium text-background-inverse text-base`}>
              {group.payorDisplay}
            </span>
            {group.payorDetail && (
              <span className={tw`text-content-subtle text-sm`}>{group.payorDetail}</span>
            )}
          </div>
          {group.rankLabel && <Badge variant="info" content={group.rankLabel} />}
        </div>

        <div className={tw`text-content-subtle text-sm`}>{metaSegments.join(' · ')}</div>

        {sourceCoverages.length > 0 && (
          <div className={tw`flex flex-col gap-2 pt-2`}>
            <div className={tw`font-medium text-content-subtle text-xs uppercase`}>Sources</div>
            {sourceCoverages.map((coverage) => (
              <CoverageSourceRow key={coverage.id} coverage={coverage} />
            ))}
          </div>
        )}
      </div>
    </ContentCard>
  );
}

function coveragesWithSources(coverages: Array<CoverageModel>): Array<CoverageModel> {
  const seen = new Set<string>();
  return coverages.filter((c) => {
    const binaryId = c.sourceBinaryId;
    if (!binaryId || seen.has(binaryId)) {
      return false;
    }
    seen.add(binaryId);
    return true;
  });
}

function CoverageSourceRow({ coverage }: { coverage: CoverageModel }) {
  const network = coverage.externalNetworkSourceName;
  const daysAgo = formatDaysAgo(coverage.lastUpdated);
  const segments = compact([network ? capitalize(network) : undefined, daysAgo]);

  return (
    <div className={tw`flex items-center gap-2 whitespace-nowrap text-sm`}>
      <ResponsiveSourceDocumentLink model={coverage} binaryId={coverage.sourceBinaryId} />
      {segments.length > 0 && (
        <span className={tw`text-content-subtle`}>{segments.join(' · ')}</span>
      )}
    </div>
  );
}
